/**
 * @fileoverview Members' ratings of a place (US-06 / F-G-02, 2026-09-27,
 * OQ-52): one to five, one per member per place.
 *
 * One per member, not one per visit: someone who goes five times still weighs
 * one, so the average is what the people who went think, not how often the
 * keenest of them went. The average is shown to one decimal place.
 */

export const RATING_MIN = 1;
export const RATING_MAX = 5;

export type RatingCheck = { ok: true; value: number } | { ok: false; reason: string };

/** A whole number from `RATING_MIN` to `RATING_MAX`. */
export function checkRating(raw: unknown): RatingCheck {
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < RATING_MIN || raw > RATING_MAX) {
    return { ok: false, reason: `評価は${RATING_MIN}から${RATING_MAX}で選んでください` };
  }
  return { ok: true, value: raw };
}

/**
 * The average of `ratings` to one decimal place, and how many there are.
 * `null` for none — not 0, which would read as the worst.
 */
export function summarizeRatings(ratings: readonly number[]): {
  average: number | null;
  count: number;
} {
  const sum = ratings.reduce((total, rating) => total + rating, 0);
  return { average: averageRating(sum, ratings.length), count: ratings.length };
}

/**
 * The average of ratings adding up to `sum`, to one decimal place; `null` for
 * none. What the map's list carries (the database sums, 2026-09-28) and what
 * the card shows (`summarizeRatings`) round the same way here, so the two can
 * never show a place differently.
 */
export function averageRating(sum: number, count: number): number | null {
  if (count <= 0) return null;
  return Math.round((sum / count) * 10) / 10;
}
